import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AddProject.css';
import { api } from '../../utils/authApi';

const AddProject = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    projectName: '',
    projectLocation: '',
    selectedUser: null,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch all users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers([]);
      setShowDropdown(false);
    } else {
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
      setShowDropdown(filtered.length > 0);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/');
      const data = await response.json();
      
      // Transform the array data to objects
      const transformedUsers = data.Message.map(user => ({
        id: user[0],
        name: user[1],
        email: user[2],
        role: user[4]
      })).filter(user => user.role === 'client'); // Only show clients
      
      setUsers(transformedUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setErrorMessage('Failed to load users. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleSelectUser = (user) => {
    setFormData(prev => ({
      ...prev,
      selectedUser: user
    }));
    setSearchTerm(`${user.name} (${user.email})`);
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.projectName || !formData.projectLocation || !formData.selectedUser) {
      setErrorMessage('Please fill in all required fields and select a client');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      // Create the project (JWT token automatically included)
      const projectData = {
        user_id: formData.selectedUser.id,
        location: formData.projectLocation,
        email: formData.selectedUser.email,
        name: formData.projectName
      };

      const response = await api.post('/projects/add', projectData);
      
      if (!response.ok) {
        throw new Error('Failed to create project');
      }
      
      setSuccessMessage(`Project "${formData.projectName}" created successfully for ${formData.selectedUser.name}!`);
      
      // Reset form
      setFormData({
        projectName: '',
        projectLocation: '',
        selectedUser: null,
      });
      setSearchTerm('');
      
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage(error.message || 'An error occurred while creating the project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      projectName: '',
      projectLocation: '',
      selectedUser: null,
    });
    setSearchTerm('');
    setSuccessMessage('');
    setErrorMessage('');
    setShowDropdown(false);
  };

  return (
    <div className="project-page-wrapper">
      <nav className="dashboard-nav">
        <div className="container nav-content">
          <h2 className="logo">EngineeringCubes</h2>
          <button onClick={() => navigate('/engineer-dashboard')} className="btn btn-secondary">
            Back to Dashboard
          </button>
        </div>
      </nav>

      <div className="project-page-content">
        <div className="project-form-container">
          <div className="project-form-card">
            <h2 className="project-form-title">Add New Project for Existing Client</h2>
            
            <form onSubmit={handleSubmit} className="project-input-form">
              {/* Client Search */}
              <div className="project-form-group">
                <label htmlFor="clientSearch" className="project-form-label">
                  Search Client <span className="project-required">*</span>
                </label>
                <div className="project-search-wrapper">
                  <input
                    type="text"
                    id="clientSearch"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="project-form-input"
                    placeholder="Search by name or email..."
                    autoComplete="off"
                  />
                  {showDropdown && (
                    <div className="project-search-dropdown">
                      {filteredUsers.map(user => (
                        <div
                          key={user.id}
                          className="project-dropdown-item"
                          onClick={() => handleSelectUser(user)}
                        >
                          <div className="project-user-name">{user.name}</div>
                          <div className="project-user-email">{user.email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formData.selectedUser && (
                  <div className="project-selected-info">
                    <strong>Selected Client:</strong> {formData.selectedUser.name} ({formData.selectedUser.email})
                  </div>
                )}
              </div>

              {/* Project Name */}
              <div className="project-form-group">
                <label htmlFor="projectName" className="project-form-label">
                  Project Name <span className="project-required">*</span>
                </label>
                <input
                  type="text"
                  id="projectName"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleInputChange}
                  className="project-form-input"
                  placeholder="Enter project name"
                  required
                />
              </div>

              {/* Project Location */}
              <div className="project-form-group">
                <label htmlFor="projectLocation" className="project-form-label">
                  Project Location <span className="project-required">*</span>
                </label>
                <input
                  type="text"
                  id="projectLocation"
                  name="projectLocation"
                  value={formData.projectLocation}
                  onChange={handleInputChange}
                  className="project-form-input"
                  placeholder="Enter project location (e.g., Building A, Floor 3)"
                  required
                />
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="project-message project-message-error">
                  {errorMessage}
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="project-message project-message-success">
                  {successMessage}
                </div>
              )}

              {/* Action Buttons */}
              <div className="project-form-actions">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary project-btn-submit"
                >
                  {isLoading ? 'Creating Project...' : 'Create Project'}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={isLoading}
                  className="btn btn-secondary project-btn-reset"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProject;